import React from 'react';
import { motion } from 'framer-motion';
import { Monitor, Search, Share2, Newspaper, ShoppingCart, Smartphone, Globe } from 'lucide-react';
import { slideUp } from '../utils/animations';
const channels = [{
  icon: Monitor,
  title: 'Trademark Registry',
  desc: 'USPTO, WIPO, EUIPO',
  iconBg: 'bg-blue-500'
}, {
  icon: Search,
  title: 'Search Engines',
  desc: 'Google, Bing, DuckDuckGo',
  iconBg: 'bg-indigo-500'
}, {
  icon: Share2,
  title: 'Social Media',
  desc: 'Twitter, Instagram, TikTok, Facebook, Threads, LinkedIn',
  iconBg: 'bg-pink-500'
}, {
  icon: Newspaper,
  title: 'News & Blogs',
  desc: 'Media mentions & articles',
  iconBg: 'bg-orange-500'
}, {
  icon: ShoppingCart,
  title: 'Marketplaces',
  desc: 'Amazon, eBay, Alibaba',
  iconBg: 'bg-green-500'
}, {
  icon: Smartphone,
  title: 'App Stores',
  desc: 'iOS & Android apps',
  iconBg: 'bg-violet-500'
}, {
  icon: Globe,
  title: 'Domains & Web',
  desc: 'Registrations & content',
  iconBg: 'bg-teal-500'
}];
export function CoverageSection() {
  return <section className="py-16 md:py-24 bg-white relative overflow-hidden" data-id="element-78">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-slate-50 pointer-events-none" data-id="element-79"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-80">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16" data-id="element-81">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={slideUp} className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 tracking-tight" data-id="element-82">
            Monitor <span className="text-indigo-600" data-id="element-83">7 critical channels</span>
          </motion.h2>
          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={slideUp} className="text-base md:text-lg text-slate-600 leading-relaxed" data-id="element-84">
            Comprehensive trademark protection across every platform where
            infringement can occur.
          </motion.p>
        </div>

        {/* Animated Wave Pattern - All Screens */}
        <div className="relative mb-12" data-id="element-85">
          {/* Scanning Wave Animation */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-86">
            <motion.div animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0, 0.3]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-64 h-64 rounded-full border-4 border-indigo-500" data-id="element-87" />
            <motion.div animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0, 0.3]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute w-64 h-64 rounded-full border-4 border-violet-500" data-id="element-88" />
          </div>

          {/* Channel Cards - Responsive Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 relative z-10" data-id="element-89">
            {channels.map((channel, i) => <motion.div key={channel.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.08
          }} whileHover={{
            y: -8,
            scale: 1.02
          }} className="bg-white rounded-2xl p-5 md:p-6 shadow-lg border border-slate-100 hover:shadow-xl hover:border-indigo-100 transition-all duration-300 cursor-default group" data-id="element-90">
                <div className="flex items-start justify-between mb-4" data-id="element-91">
                  <motion.div whileHover={{
                rotate: [0, -15, 15, -15, 0]
              }} transition={{
                duration: 0.5
              }} className={`w-12 h-12 md:w-14 md:h-14 rounded-xl ${channel.iconBg} flex items-center justify-center text-white shadow-lg group-hover:shadow-xl`} data-id="element-92">
                    <channel.icon className="w-6 h-6 md:w-7 md:h-7" data-id="element-93" />
                  </motion.div>

                  {/* Scanning Indicator */}
                  <div className="flex items-center gap-1.5" data-id="element-94">
                    <motion.div animate={{
                  scale: [1, 1.4, 1],
                  opacity: [0.5, 1, 0.5]
                }} transition={{
                  duration: 2,
                  repeat: Infinity
                }} className="w-2 h-2 bg-green-500 rounded-full" data-id="element-95" />
                    <span className="text-xs text-slate-400 font-medium" data-id="element-96">
                      Active
                    </span>
                  </div>
                </div>

                <h3 className="text-base md:text-lg font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors" data-id="element-97">
                  {channel.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed" data-id="element-98">
                  {channel.desc}
                </p>

                {/* Progress Bar Animation */}
                <div className="mt-4 h-1 bg-slate-100 rounded-full overflow-hidden" data-id="element-99">
                  <motion.div initial={{
                width: '0%'
              }} whileInView={{
                width: '100%'
              }} viewport={{
                once: true
              }} transition={{
                duration: 1.5,
                delay: i * 0.1
              }} className={`h-full ${channel.iconBg} rounded-full`} data-id="element-100" />
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* Simple Stats - No Emojis */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center" data-id="element-101">
          <p className="text-slate-600 text-sm md:text-base" data-id="element-102">
            Monitoring{' '}
            <span className="font-bold text-slate-900" data-id="element-103">180+ countries</span>{' '}
            with{' '}
            <span className="font-bold text-slate-900" data-id="element-104">
              24/7 real-time scanning
            </span>{' '}
            and{' '}
            <span className="font-bold text-slate-900" data-id="element-105">
              &lt;1 hour detection time
            </span>
          </p>
        </motion.div>
      </div>
    </section>;
}